<template>
  <!-- Zentraler Dialog „E-Mail-Empfang & Benachrichtigungen" (Kundenfeedback
       15.10.2026): bündelt, was bisher auf Profil, Kommunikations-
       einstellungen und Organigramm verteilt war – eigene Themen, Themen der
       Postfächer und wohin die Mails der Abteilungen gehen.
       Kundenfeedback Kanzlei Polz 18.10.2026 Teil 2 (II): „Gibt es einen
       Unterschied zwischen Mail & Benachrichtigungen (links oben) und den
       Einstellungen E-Mail-Empfang & Benachrichtigungen?" – Nein. Der Dialog
       heißt deshalb genauso, hat dieselben Reiter in derselben Reihenfolge
       (erst „Für mich", dann „Für Ihr Unternehmen") und ist nur der kürzere
       Weg zu denselben Einstellungen. -->
  <v-dialog
    :fullscreen="display.smAndDown.value"
    max-width="980"
    :model-value="modelValue"
    scrollable
    @update:model-value="$event ? emit('update:modelValue', true) : close()"
  >
    <v-card class="ncd" data-testid="notify-center-dialog" rounded="lg">
      <header class="ncd__head">
        <span class="ncd__icon"><MailBellIcon :size="24" /></span>
        <h2 class="ncd__title">{{ $gt('E-Mail-Empfang & Benachrichtigungen') }}</h2>
        <div class="ncd__actions">
          <HelpLink article-id="wer-bekommt-mails" new-tab />
          <button :aria-label="$gt('Schließen')" class="ncd__close" type="button" @click="close">
            <X :size="20" />
          </button>
        </div>
        <p class="ncd__lead">
          {{ sections.organization
            ? $gt('Dieselben Einstellungen wie unter Einstellungen › E-Mail-Empfang & Benachrichtigungen – hier nur schneller erreichbar: erst, was Sie selbst erhalten, dann, was an die Postfächer Ihres Unternehmens geht.')
            : $gt('Wählen Sie, worüber Sie per E-Mail informiert werden.') }}
        </p>
      </header>

      <!-- Beschriftete Reiter wie in der Korrespondenz der Akte (Kundenfeedback
           Kanzlei Polz 21.10.2026, II): erst „für mich" – samt Abwesenheits-
           Manager, der bisher nur im Profil lag –, dann die Organisation. -->
      <nav :aria-label="$gt('Bereiche')" class="ncd__tabs" role="tablist">
        <span v-for="group in tabGroups" :key="group.key" class="ncd__tabs-set">
          <span v-if="sections.organization" class="ncd__tabs-group" :class="`ncd__tabs-group--${group.key}`">{{ $gt(group.label) }}</span>
          <button
            v-for="tab in group.tabs"
            :key="tab.key"
            :aria-selected="activeTab === tab.key ? 'true' : 'false'"
            class="ncd__tab"
            :class="{ 'ncd__tab--on': activeTab === tab.key }"
            :data-testid="`notify-center-tab-${tab.key}`"
            role="tab"
            type="button"
            @click="selectTab(tab.key)"
          >
            <component :is="TAB_ICONS[tab.key]" aria-hidden="true" :size="15" />
            <span>{{ $gt(tab.label) }}</span>
          </button>
        </span>
      </nav>

      <v-card-text class="ncd__body">
        <section v-if="activeTab === 'benachrichtigungen'" aria-labelledby="ncd-personal" class="ncd__section">
          <h3 id="ncd-personal" class="ncd__section-title">
            <UserRound :size="16" />
            {{ $gt('Für mich') }}
          </h3>
          <div class="ncd__panel">
            <NotificationSection />
          </div>
        </section>

        <section v-else-if="activeTab === 'abwesenheit'" aria-labelledby="ncd-absence" class="ncd__section" data-testid="notify-center-absence">
          <h3 id="ncd-absence" class="ncd__section-title">
            <CalendarOff :size="16" />
            {{ $gt('Abwesenheit & Vertretung') }}
          </h3>
          <p class="ncd__hint">
            {{ $gt('Wie eine Abwesenheitsregel in Outlook: Während des Zeitraums erhält die Vertretung Ihre Benachrichtigungen und Zuweisungen, Absender bekommen auf Wunsch eine automatische Abwesenheitsnotiz. Dieselbe Einstellung wie in „Mein Profil“.') }}
          </p>
          <div class="ncd__panel">
            <SubstitutionEditor :user-id="null" @dirty="absenceDirty = $event" @saved="absenceDirty = false" />
          </div>
        </section>

        <section v-else-if="activeTab === 'ansicht'" class="ncd__section" data-testid="notify-center-view">
          <div class="ncd__panel">
            <CorrespondenceSection />
          </div>
        </section>

        <section v-else-if="activeTab === 'postfaecher'" aria-labelledby="ncd-org" class="ncd__section">
          <h3 id="ncd-org" class="ncd__section-title">
            <Building2 :size="16" />
            {{ $gt('Für Ihr Unternehmen – gilt für alle') }}
          </h3>
          <!-- Reihenfolge wie auf der Seite (Regel 2 vor Regel 3): erst wohin
               die E-Mails ohne Sachbearbeiter gehen, dann welche Themen. -->
          <div class="ncd__org">
            <div class="ncd__panel ncd__panel--mailboxes">
              <button class="ncd__link" data-testid="notify-center-fallback" type="button" @click="openFallback">
                <Inbox :size="15" />
                <span>{{ $gt('Standard-Empfänger ohne Sachbearbeiter festlegen') }}</span>
                <ArrowRight class="ncd__link-chev" :size="14" />
              </button>
              <v-skeleton-loader v-if="loadingMailboxes" type="list-item-two-line, list-item-two-line" />
              <p v-else-if="mailboxError" class="ncd__error">{{ mailboxError }}</p>
              <DepartmentMailboxList
                v-else
                class="ncd__mailboxes"
                :editable="sections.settingsPage"
                :mailboxes="mailboxes"
                @edit="openMailboxes"
              />
            </div>
            <MailboxTopicsCard :anchor-id="null" personal-inline />
          </div>
        </section>

        <button
          v-if="sections.settingsPage"
          class="ncd__more"
          data-testid="notify-center-settings-page"
          type="button"
          @click="openSettingsPage"
        >
          <Settings2 :size="15" />
          <span>{{ $gt('Alle Kommunikationseinstellungen öffnen') }}</span>
          <ArrowRight :size="14" />
        </button>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
  import type { DepartmentMailbox } from '@/pages/pageCommunicationSettings/departmentMailboxModel'
  import { ArrowRight, Bell, Building2, CalendarOff, Inbox, Rows3, Settings2, UserRound, X } from 'lucide-vue-next'
  import { computed, ref, watch } from 'vue'
  import { useRouter } from 'vue-router'
  import { useDisplay } from 'vuetify'
  import HelpLink from '@/components/help/HelpLink.vue'
  import MailBellIcon from '@/components/notifyCenter/MailBellIcon.vue'
  import { notifyCenterSections, notifyCenterTabGroups, type NotifyCenterTabKey, notifyCenterTabs } from '@/components/notifyCenter/notifyCenterModel'
  import SubstitutionEditor from '@/components/substitution/SubstitutionEditor.vue'
  import { useRoleView } from '@/composables/useRoleView'
  import { gt } from '@/i18n/gt'
  import DepartmentMailboxList from '@/pages/pageCommunicationSettings/DepartmentMailboxList.vue'
  import MailboxTopicsCard from '@/pages/pageCommunicationSettings/MailboxTopicsCard.vue'
  import CorrespondenceSection from '@/pages/pageUserSettings/CorrespondenceSection.vue'
  import NotificationSection from '@/pages/pageUserSettings/NotificationSection.vue'
  import { loadFallbackSettings } from '@/services/notificationRecipients'
  import { useAuthStore } from '@/stores/authStore'

  const props = defineProps<{ modelValue: boolean }>()
  const emit = defineEmits<{ 'update:modelValue': [value: boolean] }>()

  const router = useRouter()
  const display = useDisplay()
  const authStore = useAuthStore()
  const { isPureMandant } = useRoleView()

  const sections = computed(() => notifyCenterSections({
    isLightUser: authStore.isLightUser,
    isPureMandant: isPureMandant.value,
    isManager: authStore.isAdministrator || authStore.isCompanyManager || authStore.isLocationManager,
  }))
  const tabs = computed(() => notifyCenterTabs(sections.value))
  const tabGroups = computed(() => notifyCenterTabGroups(tabs.value))
  const activeTab = ref<NotifyCenterTabKey>('benachrichtigungen')
  const TAB_ICONS = { benachrichtigungen: Bell, abwesenheit: CalendarOff, ansicht: Rows3, postfaecher: Building2 }
  /** Ungespeicherte Vertretung: vor dem Schließen nachfragen. */
  const absenceDirty = ref(false)

  const mailboxes = ref<DepartmentMailbox[]>([])
  const loadingMailboxes = ref(false)
  const mailboxError = ref('')

  async function loadMailboxes () {
    loadingMailboxes.value = true
    mailboxError.value = ''
    try {
      mailboxes.value = (await loadFallbackSettings()).mailboxes ?? []
    } catch (error) {
      mailboxError.value = (error as Error)?.message || gt('Die Postfächer konnten nicht geladen werden.')
    } finally {
      loadingMailboxes.value = false
    }
  }

  // Bei jedem Öffnen frisch laden: Adressen können sich geändert haben.
  watch(() => props.modelValue, open => {
    if (open && sections.value.organization) loadMailboxes()
  }, { immediate: true })

  function close () {
    if (absenceDirty.value && !window.confirm(gt('Die Änderungen an Ihrer Vertretung sind noch nicht gespeichert. Trotzdem schließen?'))) return
    absenceDirty.value = false
    emit('update:modelValue', false)
  }

  function selectTab (key: NotifyCenterTabKey) {
    if (key === activeTab.value) return
    if (absenceDirty.value && !window.confirm(gt('Die Änderungen an Ihrer Vertretung sind noch nicht gespeichert. Trotzdem wechseln?'))) return
    absenceDirty.value = false
    activeTab.value = key
  }

  function openSettingsPage () {
    close()
    router.push({ name: 'PageCommunicationSettings' }).catch(() => {})
  }

  function openMailboxes () {
    close()
    router.push({ name: 'PageCommunicationSettings', query: { bereich: 'postfaecher' } }).catch(() => {})
  }

  function openFallback () {
    close()
    router.push({ name: 'PageCommunicationSettings', query: { bereich: 'empfaenger' } }).catch(() => {})
  }
</script>

<style lang="scss" scoped>
.ncd { --ncd-accent: #0076e5; overflow: hidden; }

.ncd__head {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  grid-template-areas:
    "icon title actions"
    "icon lead  lead";
  column-gap: 14px;
  align-items: center;
  padding: 20px 22px 16px;
  border-bottom: 1px solid var(--border-grey-color, #e4e7ec);
  background: linear-gradient(180deg, #f0f6ff 0%, #fff 100%);
}
.ncd__icon {
  grid-area: icon; align-self: start;
  display: grid; place-items: center;
  width: 48px; height: 48px; border-radius: 14px;
  color: #fff; background: var(--ncd-accent);
  --mail-bell-ring: var(--ncd-accent);
}
.ncd__title { grid-area: title; margin: 0; font-size: 1.15rem; font-weight: 750; color: var(--dark-color, #1f2937); hyphens: manual; }
.ncd__actions { grid-area: actions; display: flex; align-items: center; gap: 4px; }
.ncd__lead { grid-area: lead; margin: 4px 0 0; font-size: 0.85rem; line-height: 1.5; color: var(--grey-color, #5f5f5f); }
@media (max-width: 600px) {
  .ncd__head {
    grid-template-areas:
      "icon title actions"
      "lead lead  lead";
    padding: 16px;
  }
  .ncd__icon { width: 40px; height: 40px; border-radius: 12px; align-self: center; }
  .ncd__title { font-size: 1rem; }
  .ncd__lead { margin-top: 10px; }
  .ncd__body { padding: 16px !important; }
}
.ncd__close {
  display: grid; place-items: center; width: 34px; height: 34px;
  border: none; border-radius: 10px; background: transparent; cursor: pointer;
  color: var(--grey-color, #5f5f5f);
  transition: background-color var(--duration-fast, 150ms);
  &:hover { background: #eef2f7; }
  &:focus-visible { outline: 2px solid var(--ncd-accent); outline-offset: 2px; }
}

.ncd__body { padding: 20px 22px 24px !important; display: flex; flex-direction: column; gap: 22px; }

// Reiter wie die beschrifteten Unterreiter der Akte (Kundenfeedback 21.10.2026, II).
.ncd__tabs {
  display: flex; flex-wrap: wrap; gap: 4px;
  padding: 10px 22px 0;
  border-bottom: 1px solid var(--border-grey-color, #e4e7ec);
  background: #fff;
}
.ncd__tab {
  display: inline-flex; align-items: center; gap: 7px; flex: none;
  padding: 9px 14px 10px;
  border: none; border-bottom: 2px solid transparent; background: none;
  color: var(--grey-color, #5f5f5f); font-size: 0.84rem; font-weight: 600; cursor: pointer;
  transition: color var(--duration-fast, 150ms), border-color var(--duration-fast, 150ms);
  &:hover { color: var(--dark-color, #1f2937); }
  &:focus-visible { outline: 2px solid var(--ncd-accent); outline-offset: -2px; border-radius: 6px; }
}
.ncd__tab--on { color: var(--ncd-accent); border-bottom-color: var(--ncd-accent); }
/* Gruppen „Für mich" / „Für Ihr Unternehmen" vor den Reitern – derselbe
   Aufbau wie die Abschnitte der Einstellungsseite. */
.ncd__tabs-set { display: inline-flex; align-items: stretch; gap: 4px; flex: none; }
.ncd__tabs-set + .ncd__tabs-set { margin-left: 10px; }
.ncd__tabs-group {
  align-self: center; flex: none;
  margin: 0 4px 0 0; padding: 3px 9px; border-radius: 999px;
  font-size: 0.66rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
  color: #1d4ed8; background: #dbeafe;
  &.ncd__tabs-group--org { color: #0f766e; background: #ccfbf1; }
}
@media (max-width: 600px) {
  .ncd__tabs { padding: 6px 12px 0; }
  .ncd__tab { padding: 8px 10px; }
}

.ncd__hint { margin: -2px 0 12px; font-size: 0.82rem; line-height: 1.5; color: var(--grey-color, #5f5f5f); }

.ncd__more {
  display: inline-flex; align-items: center; gap: 8px; align-self: flex-start;
  padding: 8px 12px; border: 1px solid var(--border-grey-color, #e4e7ec); border-radius: 10px;
  background: #fff; color: var(--ncd-accent); font-size: 0.82rem; font-weight: 600; cursor: pointer;
  transition: background-color var(--duration-fast, 150ms);
  &:hover { background: #f0f6ff; }
  &:focus-visible { outline: 2px solid var(--ncd-accent); outline-offset: 2px; }
}

.ncd__section-title {
  display: flex; align-items: center; gap: 8px;
  margin: 0 0 10px;
  font-size: 0.72rem; font-weight: 750; letter-spacing: 0.08em; text-transform: uppercase;
  color: #475569;
}

.ncd__panel {
  padding: 18px 20px;
  border: 1px solid var(--border-grey-color, #e4e7ec);
  border-radius: 16px;
  background: #fff;
}

.ncd__org {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.15fr);
  gap: 16px;
  align-items: start;
}
@media (max-width: 760px) {
  .ncd__org { grid-template-columns: 1fr; }
}

.ncd__mailboxes { margin-top: 0; }
.ncd__error { margin: 0; font-size: 0.8rem; color: #b91c1c; }

.ncd__link {
  display: flex; align-items: center; gap: 8px; width: 100%;
  margin-bottom: 14px; padding: 10px 12px;
  border: 1px solid #ccfbf1; border-radius: 10px;
  background: #f0fdfa; color: #0f766e;
  font-size: 0.82rem; font-weight: 600; text-align: left; cursor: pointer;
  transition: background-color var(--duration-fast, 150ms), border-color var(--duration-fast, 150ms);
  &:hover { background: #ccfbf1; border-color: #99f6e4; }
  &:focus-visible { outline: 2px solid #0d9488; outline-offset: 2px; }
  span { flex: 1; }
}
.ncd__link-chev { flex-shrink: 0; }

@media (prefers-reduced-motion: reduce) {
  .ncd__close, .ncd__link, .ncd__tab, .ncd__more { transition: none; }
}
</style>
